const template = `
<div class="slide-panel__backdrop"></div>
<div class="slide-panel__panel slide-panel__panel--right" tabindex="0">
  <div class="slide-panel__content">
    <div class="slide-panel__inner">
        <div class="slide-panel__title-container grid sm-grid align-center">
            <div class="slide-panel__col--title-container col text-center">
              <slot name="title"></slot>
            </div>
            <div class="slide-panel__col--close-container text-right col">
              <button class="js-close-slideout slide-panel__col--close-button" aria-label="Close">
                <slot name="close-btn">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 1L9 9L1 17" stroke="#4D4D4D"/>
                    <path d="M17 17L9 9L17 1" stroke="#4D4D4D"/>
                  </svg>
                </slot>
              </button>
            </div>
        </div>
        <div class="slide-panel__body-container">
          <slot name="content"></slot>
        </div>
    </div>
  </div>
</div>
`;

const slidePanelTemplate = document.createElement("template");
const slidePanel = document.querySelector("slide-panel");
class BaseElement extends HTMLElement {
  isReady = false;
  constructor(template, mode) {
    super();
    if (template) {
      this.template = template;
      this._shadowRoot = this.attachShadow({ mode: mode ?? "open" });
      this.render();
      this.loadStyles();
      this._unsetUrlParam();
    } else {
      this.bootstrap();
    }
  }

  _setupFocusTrap() {
    const focusableElements =
      'button, [href], input[type="email"], input[type="text"], select, textarea, [tabindex]:not([tabindex="-1"])';
    const focusableContent = [
      ...(this._shadowRoot
        ? this._shadowRoot.querySelectorAll(focusableElements)
        : []),
      ...this.querySelectorAll(focusableElements),
    ];
    if (focusableContent.length == 0) {
      return;
    }
    this._firstFocusableElement = focusableContent[0];
    this._lastFocusableElement = focusableContent[focusableContent.length - 1];
    document.addEventListener("keydown", (e) => {
      let isTabPressed = e.key === "Tab" || e.keyCode === 9;

      if (!isTabPressed) {
        return;
      }

      if (!this.contains(e.target)) {
        return;
      }

      if (e.shiftKey) {
        if (document.activeElement === this._firstFocusableElement) {
          this._lastFocusableElement.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === this._lastFocusableElement) {
          if (this._firstFocusableElement) this._firstFocusableElement.focus();
          e.preventDefault();
        }
      }
    });
  }

  bootstrap() {
    this.removeAttribute("cloak");
    this.isReady = true;
    if (this.handleReady) {
      this.handleReady();
    }
  }

  _onStylesLoaded() {
    this.bootstrap();
    this.dispatchEvent(new CustomEvent("stylesLoaded"));
    if (this.onStylesLoaded) this.onStylesLoaded();
  }

  _upgradeProperty(prop) {
    if (this.hasOwnProperty(prop)) {
      let value = this[prop];
      delete this[prop];
      this[prop] = value;
    }
  }

  _freezeBody() {
    document.querySelector("html").classList.add("frozen");
    document.querySelector("body").classList.add("frozen");
  }

  _unfreezeBody() {
    document.querySelector("html").classList.remove("frozen");
    document.querySelector("body").classList.remove("frozen");
  }

  triggerEvent(event, data) {
    this.dispatchEvent(new CustomEvent(event, { bubbles: true, detail: data }));
  }

  triggerGlobalEvent(event, data) {
    document.dispatchEvent(
      new CustomEvent(event, { bubbles: true, detail: data })
    );
  }

  loadStyles() {
    const styleSheet = this.getAttribute("shadowStylesheet");
    if (!!styleSheet) {
      const link = document.createElement("link");
      link.type = "text/css";
      link.rel = "stylesheet";
      link.onload = () => this._onStylesLoaded();
      link.href = this.getAttribute("shadowStylesheet");
      this._shadowRoot.append(link);
    } else {
      this._onStylesLoaded();
    }
  }

  render() {
    if (this.template) {
      this._shadowRoot.innerHTML = `${this.template}`;
    }
  }
}

class Deferred {
  constructor() {
    this.promise = new Promise((resolve, reject) => {
      this.reject = reject;
      this.resolve = resolve;
    });
  }
}

class SlidePanel extends BaseElement {
  timeoutId = 0;
  _initiator = null;

  constructor() {
    super(template);
    this.id = this.getAttribute("slideout-id");

    if (!this.id) {
      throw new Error("ID not specified on the Slide Out Element");
    }

    this.updateTransitionDuration(this.transitionDuration);
    this._handleDocumentClick = this._handleDocumentClick.bind(this);
    this._handleLocationHashChange = this._handleLocationHashChange.bind(this);
  }

  _upgradeProperty(prop) {
    if (this.hasOwnProperty(prop)) {
      let value = this[prop];
      delete this[prop];
      this[prop] = value;
    }
  }

  connectedCallback() {
    if (this.position) {
      this.setAnchorClass(this.position);
    } else {
      this.setAnchorClass();
    }
    if (this.transitionDuration) {
      this.updateTransitionDuration(this.transitionDuration);
    }
    this._upgradeProperty("open");
    this._upgradeProperty("transitionDuration");
    this._upgradeProperty("position");
    this.classList.remove("hidden");
    if (!this.open) {
      this._close();
    }
    this.attachEventListeners();
    this._setupFocusTrap();

    if (window.location.search.includes(this.id + "=true")) {
      this.show();
    } else {
      this._handleLocationHashChange();
    }
  }

  getRoot() {
    return this._shadowRoot.querySelector(".slide-panel");
  }

  getBackdrop() {
    return this._shadowRoot.querySelector(".slide-panel__backdrop");
  }

  hideBackdrop() {
    const backdrop = this.getBackdrop();
    if (backdrop) {
      backdrop.style.visibility = "hidden";
    }
  }

  closeFilters() {
    const filters = document.querySelectorAll("#FacetsWrapperDesktop details");
    filters.forEach((filter) => {
      filter.removeAttribute("open");
    });
  }

  freezeBody() {
    document.querySelector("html").classList.add("frozen");
    document.querySelector("body").classList.add("frozen");
  }

  unfreezeBody() {
    document.querySelector("html").classList.remove("frozen");
    document.querySelector("body").classList.remove("frozen");
  }

  showBackdrop() {
    const backdrop = this.getBackdrop();
    if (backdrop) {
      backdrop.style.opacity = "1";
      backdrop.style.removeProperty("visibility");
    }
  }

  _matchHashWithId(hash) {
    return hash.startsWith(this.id);
  }

  get isCurrentHashMatched() {
    return this._matchHashWithId(window.location.hash.replace("#", ""));
  }

  emptyIframeVideo() {
    const iframe = this.contentSlot?.querySelector(
      ".js-featured-video-carousel-iframe"
    );
    if (iframe) {
      iframe.src = "about:blank";
    }
  }

  hideRoot() {
    const root = this.getRoot();
    root === null || root === void 0
      ? void 0
      : root.setAttribute("aria-hidden", "true");
  }

  showRoot() {
    const root = this.getRoot();
    root === null || root === void 0
      ? void 0
      : root.setAttribute("aria-hidden", "false");
  }

  static get observedAttributes() {
    return ["open", "position", "transitionDuration", "type"];
  }

  attributeChangedCallback(attrName, oldVal, newVal) {
    if (oldVal === newVal) {
      return;
    }

    if (attrName === "type") {
      if (oldVal) {
        this.classList.remove("slide-panel--" + oldVal);
      }
      this.classList.add("slide-panel--" + newVal);
    }

    if (attrName === "open" && this.isReady) {
      if (newVal === "" || newVal === "true") {
        if (this.firstFocusableElement) {
          this._lastFocusElement = document.activeElement;
          setTimeout(() => {
            this.firstFocusableElement.focus();
          }, 100);
        }
        this.freezeBody();
        this.showRoot();
        this.showBackdrop();
        this.triggerEvent("slide-panel-open", { id: this.id });
      } else {
        this.unfreezeBody();
        this.hideRoot();
        this.hideBackdrop();
        this.triggerEvent("slide-panel-close", { id: this.id });
        this._unsetUrlParam();
        document.removeEventListener("keydown", this._handleKeyDown.bind(this));

        if (this._lastFocusElement) {
          setTimeout(() => {
            this._lastFocusElement.focus();
            this._lastFocusElement = null;
          }, 100);
        } else {
          document.body.focus();
        }
      }
    }
    if (attrName === "position") {
      this.removeAnchorClass(oldVal);
      this.setAnchorClass(newVal);
    }
    if (attrName === "transitionDuration") {
      this.updateTransitionDuration(newVal);
    }
  }

  _findInitiator() {
    const activeElement = document.activeElement;
    if (activeElement) {
      this._initiator = activeElement;
      return;
    }

    const links = document.querySelectorAll("a");
    for (const link of links) {
      if (link.getAttribute("href") === window.location.hash) {
        this._initiator = link;
        break;
      }
    }
  }

  _handleLocationHashChange() {
    if (this.isCurrentHashMatched) {
      this._findInitiator();
      this.show();
    } else if (this.open) {
      this._close();
    }
  }

  _unsetUrlParam() {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete(this.id);
    const currentHash = window.location.hash.replace("#", "");
    const newRelativePathQuery =
    window.location.pathname +
    `${!!searchParams.toString() ? "?" + searchParams.toString() : ""}`;
    history.pushState(null, "", newRelativePathQuery);
  }

  getAnchorClass(position) {
    let className = "slide-panel__panel";
    switch (position) {
      case "left":
        className += "--left";
        break;
      case "right":
        className += "--right";
        break;
      case "top":
        className += "--top";
        break;
      case "bottom":
        className += "--bottom";
        break;
      default:
        className += "--left";
    }
    return className;
  }

  removeAnchorClass(position) {
    const className = this.getAnchorClass(position);
    const panel = this.panel;
    panel === null || panel === void 0
      ? void 0
      : panel.classList.remove(className);
  }

  setAnchorClass(position = "left") {
    const className = this.getAnchorClass(position);
    const panel = this.panel;
    panel === null || panel === void 0
      ? void 0
      : panel.classList.add(className);
  }

  show() {
    this.open = true;
    this.freezeBody();
  }

  get open() {
    return this.getAttribute("open") === "true";
  }

  set open(value) {
    const isOpen = Boolean(value);
    if (isOpen) {
      this.setAttribute("open", "true");
      this.setAttribute("aria-hidden", false);
    } else {
      this.setAttribute("open", "false");
      this.setAttribute("aria-hidden", true);
    }
  }

  get position() {
    return this.getAttribute("position");
  }

  get contentSlot() {
    return this.querySelector('[slot="content"]') || this;
  }

  set position(value) {
    this.position = value;
  }

  get transitionDuration() {
    return this.getAttribute("transitionDuration") || "300";
  }

  set transitionDuration(value) {
    if (value) {
      this.transitionDuration = value;
    }
  }

  get handleClose() {
    this.unfreezeBody();
    return this.getAttribute("handleClose");
  }

  set handleClose(value) {
    this.handleClose = value;
  }

  attachEventListeners() {
    const backdrop = this.getBackdrop();
    backdrop === null || backdrop === void 0
      ? void 0
      : backdrop.addEventListener("click", () => {
          this._handleBackdropClick();
        });
    document.addEventListener("keydown", this._handleKeyDown.bind(this));
    const allSlideOutCloseTriggers = [
      ...this._shadowRoot.querySelectorAll(".js-close-slideout"),
      ...this.querySelectorAll(".js-close-slideout"),
    ];

    this.contentSlot.addEventListener("pointerdown", (e) => {
      if (e.target.classList.contains("js-close-slideout")) {
        this._close();
      }
    });

    allSlideOutCloseTriggers.forEach((trigger) => {
      trigger.addEventListener("click", (event) => {
        event.preventDefault();
        this._close();
      });
    });
    window.addEventListener("hashchange", this._handleLocationHashChange);
    document.addEventListener("click", this._handleDocumentClick);
  }

  disconnectedCallback() {
    window.removeEventListener("hashchange", this._handleLocationHashChange);
    document.removeEventListener("click", this._handleDocumentClick);
  }

  _handleDocumentClick(event) {
    const target = event.target.dataset.target;
    const iframe = document.querySelector(`iframe[data-target='${target}']`);

    if (
      event.target.classList.contains("js-open-slideout") &&
      this.id === target
    ) {
      this.open = true;
      if (iframe) {
        const srcAttr = event.target.dataset.videolink;
        const iframe = document.querySelector(
          `iframe[data-target='${target}']`
        );
        iframe.src = srcAttr;
        iframe.style = "opacity:1;";
      }
    }
  }

  get panel() {
    return this._shadowRoot.querySelector(".slide-panel__panel");
  }

  updateTransitionDuration() {
    const backdrop = this.getBackdrop();
    if (backdrop) {
      backdrop.style.transitionDuration = this.transitionDuration + "ms";
    }
    const panel = this.panel;
    if (panel) {
      panel.style.transitionDuration = this.transitionDuration + "ms";
    }
  }

  _handleKeyDown(event) {
    if (event.key === "Escape") {
      event.preventDefault();
      this._close();
    }
  }

  _handleBackdropClick() {
    this._close();
  }

  _close() {
    if (this.open) {
      this.open = false;
      this.unfreezeBody();
      this.emptyIframeVideo();
      this.hideRoot();
      this.hideBackdrop();
      this.closeFilters();

      if (this._initiator) {
        this._initiator.focus();
        this._initiator = null;
      }
    }
  }
}
window.customElements.define("slide-panel", SlidePanel);
